import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, Search, Mail, Phone, Globe, User, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import UserForm from '../components/UserForm';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  role: string;
  preferred_language: string;
}

export default function Users() {
  const { t } = useTranslation();
  const { userRole } = useAuth();
  const { addNotification } = useNotification();
  const [users, setUsers] = useState<User[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const canManageUsers = ['admin', 'super_admin'].includes(userRole || '');
  const isSuperAdmin = userRole === 'super_admin';

  useEffect(() => {
    fetchUsers();
  }, [searchTerm]);

  async function fetchUsers() {
    try {
      setIsLoading(true);
      setError(null);
      setIsRefreshing(true);

      let query = supabase
        .from('profiles')
        .select('*');

      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      // If admin, exclude super_admin users from results
      if (userRole === 'admin') {
        query = query.neq('role', 'super_admin');
      }

      const { data, error } = await query;
      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(error.message);
      addNotification('error', t('users.notifications.error'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }

  const handleDelete = async (id: string) => {
    const userToDelete = users.find(u => u.id === id);
    if (userToDelete?.role === 'super_admin' && !isSuperAdmin) {
      addNotification('error', 'Vous ne pouvez pas supprimer un super administrateur');
      return;
    }
    setUserToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      const userToDeleteData = users.find(u => u.id === userToDelete);
      if (userToDeleteData?.role === 'super_admin' && !isSuperAdmin) {
        throw new Error('Vous ne pouvez pas supprimer un super administrateur');
      }

      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userToDelete)
        .not('role', 'eq', 'super_admin');

      if (error) throw error;

      addNotification('success', t('users.notifications.deleted'));
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      addNotification('error', error.message || t('users.notifications.error'));
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'owner':
        return 'bg-green-100 text-green-800';
      case 'support':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const canEditUser = (user: User) => {
    if (isSuperAdmin) return true;
    if (userRole === 'admin' && user.role !== 'super_admin') return true;
    return false;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 text-center">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg inline-block">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={fetchUsers}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('users.title')}</h1>
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <button
            onClick={fetchUsers}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            disabled={isRefreshing}
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
          {canManageUsers && (
            <button
              onClick={() => {
                setSelectedUser(null);
                setShowForm(true);
              }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 w-full sm:w-auto justify-center"
            >
              <Plus size={20} />
              <span>{t('users.new')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('users.search')}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
      </div>

      {/* Mobile View */}
      <div className="block lg:hidden space-y-4">
        {users.map((user) => (
          <div key={user.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {user.first_name} {user.last_name}
                </h3>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail size={16} className="text-gray-400" />
                    <a href={`mailto:${user.email}`} className="hover:text-blue-600">
                      {user.email}
                    </a>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone size={16} className="text-gray-400" />
                      <a href={`tel:${user.phone}`} className="hover:text-blue-600">
                        {user.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Globe size={16} className="text-gray-400" />
                    <span>{t(`common.languages.${user.preferred_language}`)}</span>
                  </div>
                </div>
              </div>
              {canEditUser(user) && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setShowForm(true);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                {t(`users.roles.${user.role}`)}
              </span>
            </div>
          </div>
        ))}
        {users.length === 0 && (
          <div className="text-center py-8 text-gray-500 bg-white rounded-lg shadow">
            {t('common.noData')}
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.user')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.contact')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.role')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.language')}
              </th>
              {canManageUsers && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {user.first_name} {user.last_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <a href={`mailto:${user.email}`} className="hover:text-blue-600">
                      {user.email}
                    </a>
                  </div>
                  {user.phone && (
                    <div className="text-sm text-gray-500">
                      <a href={`tel:${user.phone}`} className="hover:text-blue-600">
                        {user.phone}
                      </a>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                    {t(`users.roles.${user.role}`)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {t(`common.languages.${user.preferred_language}`)}
                </td>
                {canManageUsers && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canEditUser(user) && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowForm(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                          title={t('common.edit')}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('common.delete')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td
                  colSpan={canManageUsers ? 5 : 4}
                  className="px-6 py-4 text-center text-gray-500"
                >
                  {t('common.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <UserForm
          user={selectedUser}
          onClose={() => {
            setShowForm(false);
            setSelectedUser(null);
          }}
          onSuccess={fetchUsers}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('users.delete')}
        message={t('users.deleteConfirm')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}